/**
 * ExtractionSettingsSection Component
 *
 * Settings of the extraction step — whether an incoming document is extracted automatically
 * or waits for a manual decision. Stored the moment the switch changes.
 */
import { useMutation, useQuery } from '@apollo/client/react';
import { InputWrapper, Loader, Toggle } from '@insio/ui-toolkit';
import { ExtractionSettingsDocument, UpdateExtractionSettingsDocument } from '@/graphql';
import { useFeatureTranslation } from '@/hooks/useFeatureTranslation';
import { useToastHelpers } from '@/hooks/use-toast-helpers';
import { useCanChangeAutomationSettings } from './use-can-change-automation-settings';

export function ExtractionSettingsSection() {
  const t = useFeatureTranslation('systemSettings.automation.extraction');
  const { showError } = useToastHelpers();
  const canChange = useCanChangeAutomationSettings();

  const { data, loading, error } = useQuery(ExtractionSettingsDocument);
  // The settings carry no id of their own, so the query is refetched instead of normalised.
  const [updateSettings, { loading: saving }] = useMutation(UpdateExtractionSettingsDocument, {
    refetchQueries: [{ query: ExtractionSettingsDocument }],
    awaitRefetchQueries: true
  });

  // Defaults mirror the backend: extraction is automatic until it is switched off.
  const autoExtract = data?.currentAccount?.extractionSettings.autoExtract ?? true;

  const handleChange = async (next: boolean) => {
    if (!canChange) return;
    try {
      // errorPolicy 'none' (vs global 'all') makes GraphQL errors throw into the catch below
      await updateSettings({ variables: { input: { autoExtract: next } }, errorPolicy: 'none' });
    } catch {
      showError(t('saveError'));
    }
  };

  if (loading && !data) {
    return <Loader.Section />;
  }

  if (error) {
    return <p className='text-sm text-error-text'>{t('loadError')}</p>;
  }

  return (
    <div className='space-y-lg'>
      <div>
        <h2 className='text-xl font-bold text-foreground-base'>{t('title')}</h2>
        <p className='text-sm text-foreground-muted mt-xs'>{t('description')}</p>
      </div>

      <InputWrapper
        label={t('autoExtract.label')}
        hint={t('autoExtract.description')}
        className='gap-xs'
      >
        <Toggle
          checked={autoExtract}
          disabled={!canChange || saving}
          onChange={handleChange}
          labelText={autoExtract ? t('autoExtract.on') : t('autoExtract.off')}
          testId='extraction-auto-extract-toggle'
        />
      </InputWrapper>
    </div>
  );
}
